/** PageEditorPage — Main orchestrator for the page customizer editor. */

import { useState, useEffect, useMemo, useLayoutEffect, useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { Eye, Globe } from 'lucide-react'
import type { EditorSection } from './usePageSections'
import { useAuthStore } from '@/lib/store/authStore'
import { usePreviewToken } from '../mobile-design/usePreviewToken'
import { usePageEditorPostMessage } from './usePageEditorPostMessage'
import { usePageConfig } from './usePageConfig'
import { usePageSections } from './usePageSections'
import { useGlobalStyles } from './useGlobalStyles'
import { PageEditorShell } from './PageEditorShell'
import { PageEditorSidebar, type PageEditorSidebarRef } from './PageEditorSidebar'
import { PagePreviewFrame, DeviceToggle, type PreviewDevice } from './PagePreviewFrame'
import { CardEditorPanel } from './CardEditorPanel'
import { CardEditorErrorBoundary, CardEditorLoadingGuard } from './CardEditorErrorBoundary'
import { CardPreviewFrame } from './CardPreviewFrame'
import { useCardEditorPostMessage } from './useCardEditorPostMessage'
import { useCardEditorState } from './useCardEditorState'
import { PublishButton } from './PublishButton'
import { DraftIndicator } from './DraftIndicator'
import { useBlockSelection } from './useBlockSelection'
import { useBlockOperations } from './useBlockOperations'
import { usePageEditorHandlers } from './usePageEditorHandlers'
import { DeleteSectionDialog, PublishDialog } from './PageEditorDialogs'
import { useStoreList } from '@/hooks/useStoreList'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

export function PageEditorPage() {
  const { pageId = 'home' } = useParams<{ pageId: string }>()
  const isCardEditor = pageId === 'product-card'

  // Auth & storefront URL
  const authMode = useAuthStore((s) => s.authMode)
  const storefrontUrl = useAuthStore((s) => s.storefrontUrl)
  const currentTenant = useAuthStore((s) => s.currentTenant)
  const activeStorefrontKey = useAuthStore((s) => s.activeStorefrontKey)
  const setActiveStorefront = useAuthStore((s) => s.setActiveStorefront)

  // Multi-storefront: resolve active store domain for preview URL
  const { data: stores = [] } = useStoreList()
  const activeStore = useMemo(
    () => stores.find((s) => s.storefront_key === activeStorefrontKey),
    [stores, activeStorefrontKey],
  )
  const scopedStorefrontUrl = activeStore?.domain
    ? `https://${activeStore.domain}`
    : storefrontUrl

  // Data hooks — MUST be called before any early return (React rules of hooks)
  const { config, isLoading: configLoading, publish, isPublishing } = usePageConfig(pageId, activeStorefrontKey)
  const {
    token: previewToken,
    isLoading: tokenLoading,
    error: tokenError,
    retry: tokenRetry,
  } = usePreviewToken(!!scopedStorefrontUrl)
  const { isReady, sendConfigUpdate, sendTokenRefresh, sendCosmeticPatch, sendAppearancePatch, sendBlockUpdate } = usePageEditorPostMessage(
    scopedStorefrontUrl ?? null,
  )

  // Section management (local state with add/reorder/delete/update)
  const {
    sections,
    addSection,
    addSectionWithBlocks,
    addChildSection,
    removeSection,
    reorderSections,
    reorderChildren,
    reorderBlocks,
    updateSection,
    moveToContainer,
    moveToRoot,
    duplicateSection,
    findByEditorId,
    toEnvelopes,
    isDirty: sectionsDirty,
    clearDraft: clearSectionsDraft,
  } = usePageSections(pageId, currentTenant ?? '', config?.sections ?? null)

  // Global styles management
  const {
    styles: globalStyles,
    setBrandColor,
    setFontFamily,
    setButtonStyle,
    setSectionSpacing,
    setPageMaxWidth,
    toThemeValues,
    isDirty: themeDirty,
    clearDraft: clearThemeDraft,
  } = useGlobalStyles(pageId, currentTenant ?? '', (config as unknown as Record<string, unknown>)?.theme as Record<string, string> | undefined)

  // Card editor PostMessage hook (sends CardTemplate to preview iframe)
  const { isReady: isCardPreviewReady, sendCardTemplate } = useCardEditorPostMessage(scopedStorefrontUrl ?? null)

  // Card editor state (fetch + draft + publish + dirty)
  const cardEditor = useCardEditorState(isCardEditor ? currentTenant : null)

  // UI state — initialToken must be declared before cardPreviewSrc useMemo
  const [initialToken, setInitialToken] = useState<string | null>(null)

  // Card preview iframe URL (stable — only set once with initialToken)
  const cardPreviewSrc = useMemo(() => {
    if (!isCardEditor || !scopedStorefrontUrl || !initialToken) return null
    const url = new URL(scopedStorefrontUrl)
    url.pathname = '/preview/card-editor'
    url.searchParams.set('preview_token', initialToken)
    url.searchParams.set('page_editor', 'true')
    return url.toString()
  }, [isCardEditor, scopedStorefrontUrl, initialToken])

  // Send card template to preview iframe when it changes (debounced 100ms)
  const cardTemplateTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  useEffect(() => {
    if (!isCardEditor) return
    clearTimeout(cardTemplateTimerRef.current)
    cardTemplateTimerRef.current = setTimeout(() => sendCardTemplate(cardEditor.template), 100)
    return () => clearTimeout(cardTemplateTimerRef.current)
  }, [isCardEditor, cardEditor.template, sendCardTemplate])

  // Cosmetic fast path: skip counter + sections ref for stable callback access
  const cosmeticSkipCountRef = useRef(0)
  const sectionsRef = useRef(sections)
  sectionsRef.current = sections

  // Combined dirty state
  const isDirty = isCardEditor ? cardEditor.isDirty : (sectionsDirty || themeDirty)

  const [device, setDevice] = useState<PreviewDevice>('desktop')
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
  const [showPublishDialog, setShowPublishDialog] = useState(false)
  const [addTarget, setAddTarget] = useState<{ parentEditorId: string; depth: number } | null>(null)
  const sidebarRef = useRef<PageEditorSidebarRef>(null)

  // Block selection + insert/delete/undo (Epic 01-03)
  const blockOpsRef = useRef<{ handleInsertBlock: Function; handleRemoveBlock: Function; handleResetToDefault: Function; pushUndoEntry: Function; pushCompoundUndo: Function; pushGapDropUndo: Function }>({ handleInsertBlock: () => {}, handleRemoveBlock: () => {}, handleResetToDefault: () => {}, pushUndoEntry: () => {}, pushCompoundUndo: () => {}, pushGapDropUndo: () => {} })
  const blockSelection = useBlockSelection({
    storefrontUrl: storefrontUrl ?? null, findByEditorId, updateSection,
    sendBlockUpdate, cosmeticSkipCountRef, selectedSectionId, sectionsRef,
    onInsertBlock: (...args: any[]) => blockOpsRef.current.handleInsertBlock(...args),
    onRemoveBlock: (...args: any[]) => blockOpsRef.current.handleRemoveBlock(...args),
    onPushUndo: (...args: any[]) => blockOpsRef.current.pushUndoEntry(...args),
    onPushCompoundUndo: (...args: any[]) => blockOpsRef.current.pushCompoundUndo(...args),
    onPushGapDropUndo: (...args: any[]) => blockOpsRef.current.pushGapDropUndo(...args),
    addSectionWithBlocks,
  })
  const blockOps = useBlockOperations({ sectionsRef, findByEditorId, updateSection, deselectBlock: blockSelection.deselectBlock, selectedBlock: blockSelection.selectedBlock, removeSection })
  blockOpsRef.current = blockOps

  // Auto-navigate sidebar to block inspector when block selected in preview
  useEffect(() => {
    if (blockSelection.selectedBlock) {
      sidebarRef.current?.navigateTo('section-config')
    }
  }, [blockSelection.selectedBlock])

  // Extracted handlers (handleSectionUpdate + handleSelectFirstBlock)
  const { selectBlock } = blockSelection
  const { handleSectionUpdate, handleSelectFirstBlock: _handleSelectFirstBlock } = usePageEditorHandlers({
    selectedSectionId,
    sectionsRef,
    updateSection,
    sendCosmeticPatch,
    sendAppearancePatch,
    cosmeticSkipCountRef,
    findByEditorId,
    sections,
    selectBlock,
  })

  // Wrap handleSelectFirstBlock to also set selection state
  const handleSelectFirstBlock = useCallback((editorId: string) => {
    _handleSelectFirstBlock(editorId)
    setSelectedSectionId(editorId)
    sidebarRef.current?.navigateTo('section-config')
  }, [_handleSelectFirstBlock])

  // Body scroll lock
  useLayoutEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  // Unsaved-changes warning (beforeunload)
  useEffect(() => {
    if (!isDirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  // Capture first token for stable iframe src
  useEffect(() => {
    if (previewToken && initialToken === null) {
      setInitialToken(previewToken)
    }
  }, [previewToken, initialToken])

  // Send token refreshes via PostMessage (not iframe reload)
  useEffect(() => {
    if (previewToken && initialToken && previewToken !== initialToken) {
      sendTokenRefresh(previewToken)
    }
  }, [previewToken, initialToken, sendTokenRefresh])

  // Send config updates to preview when local sections or theme change.
  const memoEnvelopes = useMemo(() => toEnvelopes(), [toEnvelopes])
  const memoTheme = useMemo(() => toThemeValues(), [toThemeValues])
  const envelopesJson = useMemo(() => JSON.stringify(memoEnvelopes), [memoEnvelopes])
  const themeJson = useMemo(() => JSON.stringify(memoTheme), [memoTheme])

  useEffect(() => {
    if (!config || !isReady) return
    if (cosmeticSkipCountRef.current > 0) {
      cosmeticSkipCountRef.current = 0
      return
    }
    sendConfigUpdate({
      version: config.version,
      tenantId: config.tenantId,
      pageId: config.pageId,
      sections: memoEnvelopes,
      overlays: config.overlays,
      theme: memoTheme,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- depend on serialized values for change detection
  }, [envelopesJson, themeJson, config?.version, config?.tenantId, config?.pageId, isReady])

  // Build preview iframe URL (stable — only changes when initialToken is set)
  const previewSrc = useMemo(() => {
    if (!scopedStorefrontUrl || !initialToken) return null
    const url = new URL(scopedStorefrontUrl)
    url.pathname = '/preview/customizer'
    url.searchParams.set('preview_token', initialToken)
    url.searchParams.set('page_editor', 'true')
    return url.toString()
  }, [scopedStorefrontUrl, initialToken])

  // Open full preview in new tab (uses latest token, not frozen initialToken)
  const handleFullPreview = useCallback(() => {
    if (!scopedStorefrontUrl || !previewToken) return
    const url = new URL(scopedStorefrontUrl)
    url.pathname = '/preview/customizer'
    url.searchParams.set('preview_token', previewToken)
    url.searchParams.set('page_editor', 'true')
    window.open(url.toString(), '_blank')
  }, [scopedStorefrontUrl, previewToken])

  // Publish handler — sends both sections and theme
  const handlePublish = useCallback(() => {
    if (!config) return
    const themeValues = toThemeValues()
    publish(
      {
        page_id: pageId,
        sections_json: JSON.stringify({
          version: config.version,
          tenantId: config.tenantId,
          pageId: config.pageId,
          sections: toEnvelopes(),
          overlays: config.overlays ?? [],
        }),
        theme_json: JSON.stringify(themeValues),
        storefront_key: activeStorefrontKey ?? undefined,
      },
      {
        onSuccess: () => {
          clearSectionsDraft()
          clearThemeDraft()
          setShowPublishDialog(false)
        },
      },
    )
  }, [config, pageId, toEnvelopes, toThemeValues, publish, clearSectionsDraft, clearThemeDraft, activeStorefrontKey])

  // Card editor publish handler
  const handleCardPublish = useCallback(() => {
    cardEditor.publish({ onSuccess: () => setShowPublishDialog(false) })
  }, [cardEditor])

  // Multi-storefront: scope change warning dialog state
  const [scopeSwitchTarget, setScopeSwitchTarget] = useState<string | null>(null)
  const prevStorefrontKeyRef = useRef(activeStorefrontKey)

  useEffect(() => {
    const prev = prevStorefrontKeyRef.current
    if (prev !== null && activeStorefrontKey !== prev && isDirty) {
      setScopeSwitchTarget(activeStorefrontKey)
      setActiveStorefront(prev)
    } else {
      prevStorefrontKeyRef.current = activeStorefrontKey
    }
  }, [activeStorefrontKey, isDirty, setActiveStorefront])

  const handleConfirmScopeSwitch = useCallback(() => {
    if (scopeSwitchTarget !== null) {
      prevStorefrontKeyRef.current = scopeSwitchTarget
      setActiveStorefront(scopeSwitchTarget)
      setScopeSwitchTarget(null)
    }
  }, [scopeSwitchTarget, setActiveStorefront])

  const handleCancelScopeSwitch = useCallback(() => {
    setScopeSwitchTarget(null)
  }, [])

  // Delete confirmation handler
  const handleConfirmDelete = () => {
    if (deleteTarget) {
      removeSection(deleteTarget)
      if (selectedSectionId === deleteTarget) {
        setSelectedSectionId(null)
        sidebarRef.current?.navigateTo('main')
      }
      setDeleteTarget(null)
    }
  }

  // Section select handler (used by sidebar)
  const handleSectionSelect = useCallback((editorId: string) => {
    setSelectedSectionId(editorId)
  }, [])

  // Config panel back handler
  const handleConfigBack = useCallback(() => {
    setSelectedSectionId(null)
  }, [])

  // Breakpoint edit handler
  const handleBreakpointEdit = useCallback((bp: 'desktop' | 'tablet' | 'mobile') => {
    setDevice(bp)
  }, [])

  // Find the selected section for the config panel (tree-aware)
  const selectedSection = useMemo(() => {
    if (!selectedSectionId) return null
    const lookup = findByEditorId(selectedSectionId)
    return lookup?.section ?? null
  }, [sections, selectedSectionId, findByEditorId])

  // Clear stale selection when selected section no longer exists
  useEffect(() => {
    if (selectedSectionId && !selectedSection) {
      setSelectedSectionId(null)
      sidebarRef.current?.navigateTo('main')
    }
  }, [selectedSectionId, selectedSection])

  // Open widget library scoped to a container
  const handleOpenLibraryForContainer = useCallback((parentEditorId: string, depth: number) => {
    setAddTarget({ parentEditorId, depth })
  }, [])

  // Cross-level action handlers
  const handleMoveToContainer = useCallback((editorId: string, containerId: string) => {
    moveToContainer(editorId, containerId)
  }, [moveToContainer])

  const handleMoveToRoot = useCallback((editorId: string) => {
    moveToRoot(editorId)
  }, [moveToRoot])

  const handleDuplicate = useCallback((editorId: string) => {
    const newId = duplicateSection(editorId)
    if (newId) {
      setSelectedSectionId(newId)
      sidebarRef.current?.navigateTo('section-config')
    }
  }, [duplicateSection])

  // Widget add handler
  const handleAddWidget = useCallback(
    (type: string) => {
      if (addTarget) {
        const childId = addChildSection(addTarget.parentEditorId, type)
        if (childId) {
          setSelectedSectionId(childId)
        }
        setAddTarget(null)
      } else {
        const newId = addSection(type)
        if (newId) {
          setSelectedSectionId(newId)
        }
      }
    },
    [addTarget, addChildSection, addSection],
  )

  // Early return for non-CP auth
  if (authMode !== 'control_plane') {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-gray-500">Page editor requires Control Plane authentication.</p>
      </div>
    )
  }

  // Multi-storefront: require specific store selection for page editing
  if (!isCardEditor && activeStorefrontKey === null && stores.length > 1) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <Globe className="h-12 w-12 text-muted-foreground/50" />
        <p className="text-muted-foreground">Select a store to edit its pages</p>
        <p className="text-sm text-muted-foreground/70">
          Use the store selector in the sidebar to pick a store.
        </p>
      </div>
    )
  }

  const lastPublishedAt = (config as unknown as Record<string, unknown>)?.last_published_at as string | undefined
  const deleteTargetSection = deleteTarget ? findByEditorId(deleteTarget)?.section as EditorSection | null ?? null : null

  return (
    <>
      <PageEditorShell
        backLabel={
          <>
            <span className="text-sm font-medium text-foreground">
              {isCardEditor ? 'Product Card Design' : pageId === 'home' ? 'Home Page' : pageId}
            </span>
            <DraftIndicator isDirty={isDirty} />
          </>
        }
        centerSlot={<DeviceToggle device={device} onChange={setDevice} />}
        actionsSlot={
          <>
            {!isCardEditor && (
              <button
                onClick={handleFullPreview}
                disabled={!scopedStorefrontUrl || !previewToken}
                className="h-8 w-8 flex items-center justify-center bg-white shadow-sm text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
                title="Full preview"
              >
                <Eye className="h-3.5 w-3.5" />
              </button>
            )}
            <PublishButton
              isDirty={isDirty}
              isPublishing={isCardEditor ? cardEditor.isPublishing : isPublishing}
              lastPublishedAt={isCardEditor ? undefined : lastPublishedAt}
              onClick={() => setShowPublishDialog(true)}
            />
          </>
        }
        sidebar={
          isCardEditor ? (
            <div className="w-[280px] shrink-0 m-2 ml-0 rounded-[20px] backdrop-blur-xl bg-white/70 border border-border/40 shadow-lg overflow-y-auto [scrollbar-width:none]">
              <CardEditorErrorBoundary>
                <CardEditorLoadingGuard isLoading={cardEditor.isLoading} isError={cardEditor.isError} onRetry={() => cardEditor.refetch()}>
                  <CardEditorPanel template={cardEditor.template} onTemplateChange={cardEditor.setTemplate} />
                </CardEditorLoadingGuard>
              </CardEditorErrorBoundary>
            </div>
          ) : (
            <PageEditorSidebar
              ref={sidebarRef}
              sections={sections}
              configLoading={configLoading}
              selectedSectionId={selectedSectionId}
              onSectionSelect={handleSectionSelect}
              onReorder={reorderSections}
              onReorderChildren={reorderChildren}
              onReorderBlocks={reorderBlocks}
              onDelete={setDeleteTarget}
              findByEditorId={findByEditorId}
              onAddChild={handleOpenLibraryForContainer}
              onMoveToContainer={handleMoveToContainer}
              onMoveToRoot={handleMoveToRoot}
              onDuplicate={handleDuplicate}
              onSelectFirstBlock={handleSelectFirstBlock}
              selectedSection={selectedSection}
              onSectionUpdate={handleSectionUpdate}
              onConfigBack={handleConfigBack}
              activeBreakpoint={device}
              onBreakpointEdit={handleBreakpointEdit}
              onBlockSelect={blockSelection.selectBlock}
              onResetToDefault={blockOps.handleResetToDefault}
              onAddWidget={handleAddWidget}
              addTarget={addTarget}
              onClearAddTarget={() => setAddTarget(null)}
              globalStyles={globalStyles}
              onBrandColorChange={setBrandColor}
              onFontFamilyChange={setFontFamily}
              onButtonStyleChange={setButtonStyle}
              onSectionSpacingChange={setSectionSpacing}
              onPageMaxWidthChange={setPageMaxWidth}
              blockSelection={blockSelection}
            />
          )
        }
      >
        {isCardEditor ? (
          <CardPreviewFrame src={cardPreviewSrc} isReady={isCardPreviewReady} />
        ) : (
          <PagePreviewFrame
            src={previewSrc}
            device={device}
            tokenLoading={tokenLoading}
            tokenError={tokenError}
            onTokenRetry={tokenRetry}
          />
        )}
      </PageEditorShell>

      <DeleteSectionDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        targetSection={deleteTargetSection}
      />

      <PublishDialog
        open={showPublishDialog}
        onOpenChange={setShowPublishDialog}
        onConfirm={isCardEditor ? handleCardPublish : handlePublish}
        isPublishing={isCardEditor ? cardEditor.isPublishing : isPublishing}
        isCardEditor={isCardEditor}
      />

      <AlertDialog open={scopeSwitchTarget !== null} onOpenChange={(open) => { if (!open) handleCancelScopeSwitch() }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Switch store?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unpublished changes. Switching stores will save them as a draft.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelScopeSwitch}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmScopeSwitch}>Switch Store</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
